import type {
  RoutePlan,
  RoutePlansStorage,
  RouteStopLocationsStorage,
} from '@/types/route-plan';
import { ScopedAsyncStorage } from '@/lib/storage';

const PLANS_STORAGE_KEY = '@route_plans';
const LOCATIONS_STORAGE_KEY = '@route_stop_locations';
const STORAGE_VERSION = '1.0';

/**
 * Local storage for route plans using scoped AsyncStorage.
 * One plan per date — saving a plan for a date replaces the previous one.
 * Also remembers note/to-do stop locations (keyed by normalized stop title)
 * so the wizard's locations step can prefill them next time.
 */
export class RoutePlanStore {
  private readonly storage: ScopedAsyncStorage;

  constructor(storage: ScopedAsyncStorage) {
    this.storage = storage;
  }

  /**
   * Generate a local id (Hermes has no crypto.randomUUID)
   */
  static generateId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }

  private async loadPlans(): Promise<RoutePlansStorage> {
    try {
      const raw = await this.storage.getItem(PLANS_STORAGE_KEY);
      if (!raw) {
        return { plansByDate: {}, version: STORAGE_VERSION };
      }
      return JSON.parse(raw) as RoutePlansStorage;
    } catch (error) {
      console.error('[RoutePlanStore] Error loading plans:', error);
      return { plansByDate: {}, version: STORAGE_VERSION };
    }
  }

  private async savePlans(plans: RoutePlansStorage): Promise<void> {
    await this.storage.setItem(PLANS_STORAGE_KEY, JSON.stringify(plans));
  }

  /**
   * Get the plan saved for a date (any status)
   */
  async getPlanForDate(date: string): Promise<RoutePlan | null> {
    const plans = await this.loadPlans();
    return plans.plansByDate[date] ?? null;
  }

  /**
   * Get the active plan for today, if any
   */
  async getActivePlan(todayDate: string): Promise<RoutePlan | null> {
    const plan = await this.getPlanForDate(todayDate);
    return plan && plan.status === 'active' ? plan : null;
  }

  /**
   * Save a plan, replacing any existing plan for the same date
   */
  async savePlan(plan: RoutePlan): Promise<void> {
    const plans = await this.loadPlans();
    plans.plansByDate[plan.date] = plan;
    await this.savePlans(plans);
  }

  /**
   * Apply a mutation to the plan for a date and persist the result.
   * Returns the updated plan, or null when no plan exists for the date.
   */
  async updatePlan(
    date: string,
    mutate: (plan: RoutePlan) => RoutePlan,
  ): Promise<RoutePlan | null> {
    const plans = await this.loadPlans();
    const existing = plans.plansByDate[date];
    if (!existing) return null;

    const updated: RoutePlan = {
      ...mutate(existing),
      updatedAt: new Date().toISOString(),
    };
    plans.plansByDate[date] = updated;
    await this.savePlans(plans);
    return updated;
  }

  /**
   * Delete the plan for a date
   */
  async deletePlanForDate(date: string): Promise<void> {
    const plans = await this.loadPlans();
    if (!plans.plansByDate[date]) return;
    delete plans.plansByDate[date];
    await this.savePlans(plans);
  }

  private normalizeTitle(title: string): string {
    return title.trim().toLowerCase();
  }

  private async loadLocations(): Promise<RouteStopLocationsStorage> {
    try {
      const raw = await this.storage.getItem(LOCATIONS_STORAGE_KEY);
      if (!raw) {
        return { locationsByTitle: {}, version: STORAGE_VERSION };
      }
      return JSON.parse(raw) as RouteStopLocationsStorage;
    } catch (error) {
      console.error('[RoutePlanStore] Error loading stop locations:', error);
      return { locationsByTitle: {}, version: STORAGE_VERSION };
    }
  }

  /**
   * Remember locations for note/to-do stops (keyed by normalized title)
   */
  async rememberStopLocations(
    entries: { title: string; location: string }[],
  ): Promise<void> {
    const valid = entries.filter((entry) => entry.location.trim().length > 0);
    if (valid.length === 0) return;

    const locations = await this.loadLocations();
    for (const entry of valid) {
      locations.locationsByTitle[this.normalizeTitle(entry.title)] = entry.location.trim();
    }
    await this.storage.setItem(LOCATIONS_STORAGE_KEY, JSON.stringify(locations));
  }

  /**
   * Get the remembered location for a stop title, if any
   */
  async getRememberedLocation(title: string): Promise<string | null> {
    const locations = await this.loadLocations();
    return locations.locationsByTitle[this.normalizeTitle(title)] ?? null;
  }
}
